import { ClientEntity } from "src/app/models/clients";
import {IPortfolio} from "./portfolios";

export interface IHoldings {
  date: Date;
  clientEntities: ClientEntity[];
}

export interface IHoldingsRequest {
  date: Date;
  clientEntityIds: number[];

  // Optional portfolios filter
  portfolioIds?: number[];
}

export interface IHoldingsValuationsDataResult {
  holdingsValuationData: any[];
  date: Date;
  clientEntities?: ClientEntity[];
  portfolios?: IPortfolio[];
}

export interface IHoldingsExposuresDataResult {
  holdingsExposuresData: any;
  date: Date;
  clientEntities?: ClientEntity[];

  // Optional portfolios filter
  portfolios?: IPortfolio[];
}

export interface IHoldingsTrialBalanceDataResult {
  holdingsTrialBalanceData: {};
  date: Date;
  entityIds: number[];
}

export interface IHoldingsDataState {
  selectedEntities: ClientEntity[];
  selectedPortfolios: IPortfolio[];
  loading: boolean;
  error: boolean;
  holdingsExposuresData: [];
  holdingsValuationData: any[];
  selectedDate: Date;
}

export interface IPerformanceByEquityAsset {
  unrealisedGainTotal: {
    Value: number;
  };
  unrealisedGainTotalPc: number;
  unrealisedGainCapital: {
    Value: number;
  };
  unrealisedGainCapitalPc: number;
  refIncome: {
    Value: number;
  };
  unrealisedGainCurrency: {
    Value: number;
  };
  unrealisedGainCurrencyPc: number;
  name: string;
  refBookValue: {
    Value: number;
  };
  refCurrencyCode: string;
}

export interface IPerformanceByPrivateEquityAsset {
  investorCommittedCapital: {
    value: number;
    currency: string;
    currencySymbol: string;
  };
  paidInCapital: {
    value: number;
    currency: string;
    currencySymbol: string;
  };
  uncalledCapital: {
    value: number;
    currency: string;
    currencySymbol: string;
  };
  distributions: {
    value: number;
    currency: string;
    currencySymbol: string;
  };
  residualValue: {
    value: number;
    currency: string;
    currencySymbol: string;
  };
  totalValue: {
    value: number;
    currency: string;
    currencySymbol: string;
  };
  totalReturns: {
    value: number;
    currency: string;
    currencySymbol: string;
  };
  fund: {
    name: string;
    securityId: number;
    committedCapital: {
      value: number;
      currency: string;
      currencySymbol: string;
    };
    netAssetValue: {
      value: number;
      currency: string;
      currencySymbol: string;
    };
  };
  performance: {
    piic: number;
    dpi: number;
    rvpi: number;
    tvpi: number;
  };
  history: {
    date: string;
    callTotal: number;
    distributionTotal: number;
    cumulativeNetCash: number;
  }[];
}

export interface IPerformanceByFfxAsset {
  FfxName: string;
  CurrencyCode: string;
  Quantity: string;
  FxRate: string;
  RefMarketValue: string;
  RefCurrencyCode: string;
  History: {
    Date: string;
    FxRate: string;
  }[];
  ContractRate: string;
}

export enum ExposureType {
  ALLOCATION = 1,
  VALUATION = 2
}

export enum ExposureChartType {
  PIE = 1,
  BAR = 2
}

export enum ExposureDataCaption {
  ASSETALLOCATIONS = 'Asset Allocations',
  ASSETTYPE = 'Asset Type',
  CURRENCY = 'Currency',
  LIQUIDITY = 'Liquidity',
  MATURITYPROFILE = 'Maturity Profile',
  COUPONRATE = 'Coupon Rate',
  YTMPROFILE = 'YTM Profile'
}

export enum ExposureDataType {
  ASSETALLOCATIONS = 0,
  ASSETTYPE = 1,
  CURRENCY = 2,
  LIQUIDITY = 3,
  MATURITYPROFILE = 4,
  COUPONRATE = 5,
  YTMPROFILE = 6
}

export enum CaptionType {
  PERCENTAGE = 1,
  STRING = 2
}

export enum ValueType {
  VALUE = 1,
  PERCENTAGE = 2
}

export enum SecurityTypeName {
  CASH = "Cash",
  EQUITY = "Equity",
  NON_UNITISED_INVESTMENT = "Non Unitised Investment",
  FIXED_INCOME = "Fixed Income"
}

export type Exposure = ExposureType.ALLOCATION | ExposureType.VALUATION;

export interface IExposureTabData {
  items: {
    data: {
      caption: string;
      weight: number;
    };
    children: {
      data: {
        caption: string;
        weight: number;
      };
    }[];
    type: Exposure;
    Target?: number;
    Actual?: number;
  }[];
  assetAllocations: boolean;
  hasTargets: boolean;
  highestValue?: any;
  chartType?: ExposureChartType;
  exposureType?: ExposureDataType;
  tabLabel: string
}

export class ExposureTreeNode {
  children?: ExposureTreeNode[];
  data: {
    caption: string;
    value?: number;
    weight?: number;
  };
}

export interface IAssetRevenueRequest {
  date: Date;
  assetId: number;
  entityIds?: number[];
}

export class AssetRevenue {
  InterestIncome: {
    Value: number;
  };
  Dividends: {
    Value: number;
  };
}

export interface IAssetDrilldownRequest {
  securityType: SecurityType;
  securitySubType: SecuritySubType;
  date: Date;
  assetId: number;
  assetName: string;
  performanceByAsset:
  | IPerformanceByEquityAsset
  | IPerformanceByPrivateEquityAsset
  | IPerformanceByRealEstateAsset
  | IPerformanceByFfxAsset;
  entityIds?: number[];
  portfolioIds?: number[];
}

export interface IAssetPerformanceCollection {
  [key: number]: IAssetPerformance;
}

export interface IAssetPerformance {
  assetId: number;
  assetName: string;
  performanceByAsset:
  | IPerformanceByEquityAsset
  | IPerformanceByPrivateEquityAsset
  | IPerformanceByRealEstateAsset
  | IPerformanceByFfxAsset;
  positions: IAssetPerformancePosition[];
  entityIds?: number[];
  investorActions: InvestorAction[];
}

export interface IPerformanceByRealEstateAsset {
  costPrice: number;
  marketValue: number;
  totalReturn: string;
  currencyCode: string;
  refCurrencyCode: string;
}

export interface IAssetPerformancePosition {
  close: number;
  date: string;
  high: number;
  low: number;
  open: number;
  volume: number;
}

export interface InvestorAction {
  action: number;
  actionSubType: number;
  date: string;
  portfolioSecurityAction: string;
  quantity?: number;
  refAmountValue?: number;
  unitPrice: number;
}

export enum SecurityType {
  CASH = 0,
  INDEX = 10,
  FIXEDINCOME = 20,
  EQUITY = 30,
  NONUNITISEDINVESTMENT = 40,
  OPTION = 50,
  FUTURES = 60,
  COMMODITY = 70,
  FORWARDCONTRACT = 80,
  REALESTATE = 90,
  CURRENCYPAIR = 100,
  INTERESTRATE = 110,
  LOAN = 120
}

export enum SecuritySubType {
  EQUITY = 30,
  PRIVATE_EQUITY = 35,
  REAL_ESTATE = 90,
  // INDEX
  MARKETINDEX = 10,
  CUSTOMINDEX = 11,
  // FIXED INCOME
  GOVERNMENTBOND = 20,
  TERMDEPOSIT = 21,
  CORPORATEBOND = 22,
  HYBRIDBOND = 23,
  EXCHANGETRADEDNOTE = 24,
  ZEROCOUPONBOND = 25,
  // EQUITY
  STOCK = 30,
  ETF = 31,
  MUTUALFUND = 32,
  UNITTRUST = 33,
  PRIVATECOMPANY = 34,
  // UNLISTED EQUITY
  LLP = 41,
  NOMINEE = 42,
  MANAGEDACCOUNT = 43,
  PRIVATEASSETS = 44,
  // OPTIONS
  EQUITYOPTION = 50,
  CURRENCYOPTION = 51,
  INTERESTRATEOPTION = 53,
  FUTURESOPTION = 54,
  INDEXOPTION = 55,
  // FUTURES
  INDEXFUTURES = 60,
  SINGLESTOCKFUTURES = 61,
  CURRENCYFUTURES = 62,
  COMMODITYFUTURES = 63,
  VOLATILITYFUTURES = 64,
  FIXEDINCOMEFUTURES = 65,
  INTERESTRATEFUTURES = 66,
  ANZ90DAYBANKBILLFUTURES = 67,
  // COMMODITIES
  COMMODITY = 70,
  METAL = 75,
  ENERGY = 76,
  AGRICULTURE = 77,
  LIVESTOCK = 78,
  SOFT = 79,
  // FORWARD CONTRACTS
  FOREIGNEXCHANGEFORWARD = 80,
  COMMODITYFORWARD = 81,
  // REAL ESTATE
  RESIDENTIALPROPERTY = 90,
  COMMERCIALPROPERTY = 91,
  // CURRENCY AND INTEREST RATES
  CURRENCYPAIR = 100,
  INTERESTRATE = 110,
  // LOANS
  UNSECUREDLOAN = 120,
  SECUREDLOAN = 121,
  MORTGAGE = 122
}

export const HiddenHoldingsFieldsBySecuritySubtype = {
  [SecuritySubType.PRIVATE_EQUITY]: ['_costPrice', 'costPrice'],
  [SecuritySubType.FOREIGNEXCHANGEFORWARD]: ['_costPrice', 'costPrice', 'currencyCode']
};

export class AssetRevenueState {
  [key: number]: {
    assetId: number;
    assetRevenue: AssetRevenue;
  };
}
